<template>
	<div
		:id="uid"
		:variant="variant"
		class="search-finder"
		:class="{ 'is-required': required }"
	>
		<Label
			:for="`${uid}_input`"
			class="search-finder__label"
			v-if="label"
			:labelText="label"
		/>
		<div class="search-finder__container">
			<input
				autocomplete="off"
				class="search-finder__input"
				name="search-finder"
				ref="searchFinderInput"
				type="text"
				:aria-label="label"
				:id="`${uid}_input`"
				:placeholder="placeholder"
				:label="label"
				:value="search"
				@input="updateValue"
				@keydown.down="downKeyPressed"
				@keydown.up="upKeyPressed"
			/>
			<span
				:class="[
					'search-finder__btn',
					hasSpinnerLoading ? 'has-spinner' : ''
				]"
			>
				<Button
					className="btn--icon-only"
					:btnClick="clickSearchFinderButton"
					:iconBeforeLabel="true"
					:iconClassName="'icon icon-finder'"
				/>
			</span>
			<DropdownList
				ref="dropdownlist"
				:autosuggestion="false"
				:id="`${uid}_ddl`"
				:isOpen="isOpen"
				:options="dataSuggestion"
				:value="objectId"
				v-if="hasAutosuggestion && dataSuggestion.length"
				@change="updateSelectedValue"
			/>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import DropdownList from '@/components/forms/dropdown/dropdown-list';
import DocumentClickedMixin from '@/mixins/document-click';
import Label from '@/components/forms/label/label';
import { isEmpty } from '@/utils/object';
import { mapActions } from 'vuex';

export default {
	name: 'SearchFinder',
	mixins: [
		DocumentClickedMixin(
			'updateSelectedValue',
			'hasAutosuggestion',
			'closeHandler'
		)
	],
	components: {
		Label,
		Button,
		DropdownList
	},
	model: {
		prop: 'value',
		event: 'selected'
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		value: {
			type: Object,
			default: () => {
				return { description: '' };
			}
		},
		apiOptions: {
			type: Object,
			required: true,
			default: () => {}
		},
		itemTemplate: {
			type: Object,
			default: () => {
				return {};
			}
		},
		timeout: {
			type: Number,
			default: 1000
		}
	},
	data() {
		return {
			search: '',
			objectId: this.value.id,
			currentItem: -1,
			isOpen: false,
			dataSuggestion: [],
			hasSpinnerLoading: false, //// DEV: this shoud be set to true when the button spinner is used ///,
			dataResult: {}
		};
	},
	computed: {
		hasAutosuggestion() {
			return this.dataSuggestion.length > 0;
		}
	},

	methods: {
		...mapActions('codemanager', {
			loadCodeManager: 'loadCodeManager'
		}),
		downKeyPressed() {
			if (this.hasAutosuggestion) {
				this.isOpen = true;
				if (this.currentItem < this.matches.length - 1)
					this.currentItem++;
			}
		},
		upKeyPressed() {
			if (this.hasAutosuggestion) {
				if (this.currentItem > 0) this.currentItem--;
			}
		},
		updateValue(e) {
			this.$refs['searchFinderInput'].focus();
			const value = e.target.value;

			//delay the @keyup event
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}

			this.timer = setTimeout(() => {
				this.search = value;
			}, this.timeout);

			if (this.hasAutosuggestion) {
				this.currentItem = -1;
			}
		},
		clickSearchFinderButton(e) {
			if (e) {
				e.preventDefault();
			}
			this.isOpen = false;
			this.$emit('click', true);
		},
		updateSelectedValue(id) {
			const { items } = this.dataResult || {};
			if (items?.length) {
				const objSelected = items.find(obj => {
					return obj.id === id;
				});
				if (objSelected) {
					this.$emit('selected', objSelected);
				}
				this.isOpen = false;
			}
		},
		async fetchData() {
			const { params } = this.apiOptions;
			const self = this;
			let query = { ...params };
			query.search = self.search;
			await self.loadCodeManager(query).then(res => {
				self.dataResult = res;
				self.hasSpinnerLoading = false;
				self.isOpen = self.hasAutosuggestion;
			});
		},
		closeHandler() {
			this.isOpen = false;
		}
	},
	watch: {
		value: {
			deep: true,
			immediate: true,
			handler(val) {
				this.search = val.description;
			}
		},
		search: {
			immediate: true,
			handler(value) {
				if (!value) {
					if (!isEmpty(this.value)) {
						this.$emit('selected', {});
					}
					this.isOpen = false;
					return;
				}
				const val = value.toLowerCase();
				if (
					!isEmpty(this.value) &&
					this.value?.description &&
					this.value?.description.toLowerCase() === val
				) {
					if (
						this.value.id &&
						this.value.description.toLowerCase() === val
					) {
						return;
					}

					this.$emit('selected', {});
				}
				this.fetchData();
			}
		},
		dataResult: {
			deep: true,
			immediate: true,
			handler(obj) {
				const { items } = obj || {};
				if (items?.length) {
					this.dataSuggestion = items.map(item => {
						return {
							text: item.description,
							value: item.id
						};
					});
				}
				this.isOpen = this.hasAutosuggestion;
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * Search Finder styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.search-finder {
	&__container {
		@include textfield;
		position: relative;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		.search-finder__btn.has-spinner {
			.btn--icon-only {
				min-width: auto;
				margin-right: $gutter-sm;
			}
		}
	}

	&__input {
		appearance: none;
		outline: none;
		border: none;
		cursor: pointer;
		font-size: $input-font-size;
		width: 92%;
		text-overflow: ellipsis;
		background-color: transparent;
		border-radius: $form-border-radius;
		padding: $input-padding-y $input-padding-x;
		@include input-height;

		@include mq($from: md) {
			width: 79%;
		}
	}

	&__btn {
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		min-width: 20px;
		user-select: none;
		cursor: pointer;
		outline: 0;
		border: none;
		background-color: transparent;
	}
}
</style>
